/**
 * TChartSVG — Phase 1, Component 10 of the TeachBud Representation Catalog
 *
 * Renders a T-chart (two-column comparison organizer) per §5.2 Compare & Contrast:
 *   - Bold vertical dividing line; horizontal header bar
 *   - Two column headers (editable in teacher mode)
 *   - Student mode: ruled writing lines in each column
 *   - Teacher mode: content rows provided via `leftRows` / `rightRows`
 *   - Multi-subject: compare characters, properties, countries, plant/animal, etc.
 *   - Grades K–8
 */

import React from 'react';

export interface TChartProps {
  /** Left column header. */
  leftLabel?: string;
  /** Right column header. */
  rightLabel?: string;
  /** Content rows for the left column (teacher mode). */
  leftRows?: string[];
  /** Content rows for the right column (teacher mode). */
  rightRows?: string[];
  /** Number of blank rows (student mode). Default auto from grade. */
  rowCount?: number;
  /** When true, rows are empty with ruled lines. */
  studentMode?: boolean;
  /** Override pixel width. */
  width?: number;
  /** Grade (0=K). Adjusts font sizes and row heights. */
  grade?: number;
  className?: string;
}

// ── Layout ────────────────────────────────────────────────────────────────────
const VB_W     = 520;
const PAD      = 16;
const HEADER_H = 36;
const DIVIDER_X = VB_W / 2;

function rowHeight(grade: number) {
  if (grade <= 1) return 52;
  if (grade <= 3) return 42;
  return 34;
}

function defaultRows(grade: number) {
  if (grade <= 1) return 3;
  if (grade <= 3) return 4;
  return 5;
}

const HEADER_FILL = '#1E40AF';   // blue-800
const LEFT_FILL   = '#EFF6FF';   // blue-50
const RIGHT_FILL  = '#F5F3FF';   // violet-50
const DIVIDER     = '#374151';   // gray-700
const HEADER_LINE = '#D1D5DB';
const LINE_COLOR  = '#E5E7EB';
const TEXT_COLOR  = '#1E3A5F';

export function TChartSVG({
  leftLabel  = 'Column 1',
  rightLabel = 'Column 2',
  leftRows   = [],
  rightRows  = [],
  rowCount,
  studentMode = true,
  width,
  grade = 3,
  className = '',
}: TChartProps) {
  const rowH   = rowHeight(grade);
  const nRows  = rowCount ?? (studentMode ? defaultRows(grade) : Math.max(leftRows.length, rightRows.length, 3));
  const BODY_H = nRows * rowH;
  const VB_H   = HEADER_H + BODY_H + PAD;

  const svgW = width ?? VB_W;
  const svgH = width ? Math.round((VB_H / VB_W) * width) : VB_H;

  const HEADER_FS = grade <= 2 ? 14 : 12;
  const BODY_FS   = grade <= 2 ? 12 : 10;

  const colW = (VB_W - 2 * PAD) / 2;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={svgW} height={svgH}
      className={className}
      role="img"
      aria-label={`T-chart comparing ${leftLabel} and ${rightLabel}`}
    >
      <title>T-chart: {leftLabel} | {rightLabel}</title>

      {/* Outer border */}
      <rect x={PAD} y={0} width={VB_W - 2 * PAD} height={VB_H} rx={6} fill="none" stroke="#D1D5DB" strokeWidth={1.5} />

      {/* Header background */}
      <rect x={PAD} y={0} width={VB_W - 2 * PAD} height={HEADER_H} rx={6} fill={HEADER_FILL} />
      {/* Clip bottom curve of header */}
      <rect x={PAD} y={HEADER_H - 8} width={VB_W - 2 * PAD} height={9} fill={HEADER_FILL} />

      {/* Header text */}
      <text x={PAD + colW / 2} y={HEADER_H / 2 + 5} textAnchor="middle" fontSize={HEADER_FS} fontFamily="Nunito, sans-serif" fontWeight={700} fill="#FFFFFF">
        {leftLabel}
      </text>
      <text x={DIVIDER_X + colW / 2} y={HEADER_H / 2 + 5} textAnchor="middle" fontSize={HEADER_FS} fontFamily="Nunito, sans-serif" fontWeight={700} fill="#FFFFFF">
        {rightLabel}
      </text>

      {/* Column backgrounds */}
      <rect x={PAD} y={HEADER_H} width={colW} height={BODY_H} fill={LEFT_FILL} />
      <rect x={DIVIDER_X} y={HEADER_H} width={colW} height={BODY_H} fill={RIGHT_FILL} />

      {/* Row dividers */}
      {Array.from({ length: nRows - 1 }).map((_, i) => {
        const ly = HEADER_H + (i + 1) * rowH;
        return (
          <line key={i} x1={PAD} y1={ly} x2={VB_W - PAD} y2={ly} stroke={HEADER_LINE} strokeWidth={1} />
        );
      })}

      {/* Bold center divider */}
      <line x1={DIVIDER_X} y1={0} x2={DIVIDER_X} y2={VB_H} stroke={DIVIDER} strokeWidth={2.5} />

      {/* Content / ruled lines */}
      {Array.from({ length: nRows }).map((_, i) => {
        const rowY = HEADER_H + i * rowH;
        const leftContent  = !studentMode ? (leftRows[i]  ?? null) : null;
        const rightContent = !studentMode ? (rightRows[i] ?? null) : null;

        if (leftContent || rightContent) {
          return (
            <g key={i}>
              {leftContent && (
                <text x={PAD + 8} y={rowY + rowH / 2 + 5} fontSize={BODY_FS} fontFamily="Inter, sans-serif" fill={TEXT_COLOR}>
                  {leftContent.slice(0, 32)}
                </text>
              )}
              {rightContent && (
                <text x={DIVIDER_X + 8} y={rowY + rowH / 2 + 5} fontSize={BODY_FS} fontFamily="Inter, sans-serif" fill={TEXT_COLOR}>
                  {rightContent.slice(0, 32)}
                </text>
              )}
            </g>
          );
        }

        // Ruled lines
        const nLines = rowH > 40 ? 2 : 1;
        const lSpacing = rowH / (nLines + 1);
        return (
          <g key={i}>
            {Array.from({ length: nLines }).map((_, li) => {
              const ly = rowY + (li + 1) * lSpacing;
              return (
                <g key={li}>
                  <line x1={PAD + 8} y1={ly} x2={DIVIDER_X - 8} y2={ly} stroke={LINE_COLOR} strokeWidth={1} />
                  <line x1={DIVIDER_X + 8} y1={ly} x2={VB_W - PAD - 8} y2={ly} stroke={LINE_COLOR} strokeWidth={1} />
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

export function parseTChartHint(hint: string): TChartProps {
  const match = hint.match(/t.?chart[:\s]+(.+?)\s+(?:vs\.?|and|versus)\s+(.+)/i);
  if (match) {
    return { leftLabel: match[1].trim().slice(0, 24), rightLabel: match[2].trim().slice(0, 24), studentMode: true };
  }
  return { leftLabel: 'Topic A', rightLabel: 'Topic B', studentMode: true };
}
